import { Module } from 'vuex';

import {
  CourseEditChapter,
  CourseEditInfo,
  CourseEditContent,
  RootState,
  Nullable,
  GlobalState,
  unreachableCase,
  LESSON_KIND,
  PRACTICE_KIND,
} from 'frontend/types';
import {
  CourseAddChapterInput,
  CourseAddContentInput,
  CourseCreateApiInput,
  CourseCreateLessonInput,
  CourseChangeStateInput,
  CourseRemoveChapterInput,
  CourseRemoveContentInput,
  CourseUpdateChapterInput,
  CourseUpdateChapterOrderInput,
  CourseUpdateContentInput,
  CourseUpdateContentOrderInput,
  ChapterUpdateContentInput,
  CoursePinUserToVersionAPIInput,
  PracticeNode,
  LessonNode,
  ContentNode,
  PracticeType,
} from 'frontend/types/authoring/requests';
import { apiResolve } from 'frontend/router';
import { apiRequestWithErrors } from 'frontend/store/modules/lessons/apiHelpers';
import { deepCopyObj } from 'frontend/utils/copy';
import { debounce } from 'frontend/utils/timing';
import { slugify, uniqueString } from 'frontend/utils/formatting';
import { LessonCreateApiInputs, LessonCreateApiResult } from 'frontend/types/lessons/requests';

interface CourseEditState {
  course?: Nullable<CourseEditInfo>;
  requestErrors: string[];
  waitingOnRequest: boolean;
}

/**
 * When moving lessons between chapters, the update function is called twice. However, we only
 * want to make one request. Therefore, use this to wrap the request so it only happens once.
 */
const courseContentUpdater = debounce((f: () => void) => f(), 50);

/*
Functions to simplify coersion between updated/edited CourseEdit* types and their backend analogues.
*/
function courseUpdateContentInput({
  slug,
  name,
  version_name,
  description,
  chapters,
}: CourseEditInfo): CourseUpdateContentInput {
  return {
    slug,
    version_name,
    description,
    title: name,
    chapters: chapters.map(chapterUpdateContentInput),
  };
}
function chapterUpdateContentInput({
  chapter_id,
  name,
  slug,
  intro_blurb,
  content,
}: CourseEditChapter): ChapterUpdateContentInput {
  return {
    slug,
    name,
    intro_blurb,
    id: chapter_id || null,
    content: content.reduce(updateContentInput, []),
  };
}
function updateContentInput(
  contentNodes: ContentNode[],
  contentNode: CourseEditContent,
): ContentNode[] {
  const lastNode = contentNodes[contentNodes.length - 1];

  switch (contentNode.kind) {
    case LESSON_KIND:
      if (contentNode.is_practice) {
        if (lastNode !== undefined && lastNode.type === 'practice') {
          // LessonPractice with existing cluster
          //   Append LessonPractice to cluster
          return [
            ...contentNodes.slice(0, -1),
            {
              type: 'practice',
              practice_sets: [
                ...lastNode.practice_sets,
                {
                  type: 'practice_lesson',
                  id: contentNode.lesson_id,
                },
              ],
            },
          ];
        } else {
          // LessonPractice with no existing cluster
          //   Append a new cluster with the LessonPractice
          return [
            ...contentNodes,
            {
              type: 'practice',
              practice_sets: [
                {
                  type: 'practice_lesson',
                  id: contentNode.lesson_id,
                },
              ],
            },
          ];
        }
      } else {
        return [
          ...contentNodes,
          {
            type: 'lesson',
            id: contentNode.lesson_id,
          },
        ];
      }
    case PRACTICE_KIND:
      if (lastNode !== undefined && lastNode.type === 'practice') {
        // PracticeSet with existing cluster
        //   Append LessonPractice to cluster
        return [
          ...contentNodes.slice(0, -1),
          {
            type: 'practice',
            practice_sets: [
              ...lastNode.practice_sets,
              {
                type: 'practice_set',
                id: contentNode.id,
              },
            ],
          },
        ];
      } else {
        // PracticeSet with NO existing cluster
        //   Append a new cluster with the PracticeSet
        return [
          ...contentNodes,
          {
            type: 'practice',
            practice_sets: [
              {
                type: 'practice_set',
                id: contentNode.id,
              },
            ],
          },
        ];
      }
    default:
      return unreachableCase(contentNode);
  }
}

interface ExtendedRootState extends RootState {
  globals: GlobalState;
}

const courseEdit: Module<CourseEditState, ExtendedRootState> = {
  namespaced: true,
  state: {
    course: null,
    requestErrors: [],
    waitingOnRequest: false,
  },
  mutations: {
    setValue(state, value: CourseEditInfo): void {
      state.course = value;
    },
    setCourseResponse(state, result: CourseEditInfo | null): void {
      state.course = result;
    },
    setErrors(state, errors: string[]): void {
      state.requestErrors = errors;
    },
    setWaiting(state, waitingOnRequest: boolean): void {
      state.waitingOnRequest = waitingOnRequest;
    },
  },
  actions: {
    resetErrors({ commit }) {
      commit('setErrors', []);
    },
    async createCourse({ commit }, { params, formData }: CourseCreateApiInput): Promise<void> {
      commit('setWaiting', true);

      const { href } = apiResolve({ name: 'course_create' });
      const [success, result, errors] = await apiRequestWithErrors(href, formData);

      commit('setErrors', errors);
      if (success) {
        commit('setCourseResponse', result?.course);
      }
      commit('setWaiting', false);

      if (!success) {
        throw new Error('Course creation request not successful.');
      }
    },
    async createCourseVersion({ commit }, { formData }: CourseCreateApiInput): Promise<void> {
      commit('setWaiting', true);

      const { href } = apiResolve({ name: 'course_create_version' });
      const [success, result, errors] = await apiRequestWithErrors(href, formData);

      commit('setErrors', errors);
      if (success) {
        commit('setCourseResponse', result?.course);
      }
      commit('setWaiting', false);

      if (!success) {
        throw new Error('Course version creation request not successful.');
      }
    },
    async changeState({ commit }, { params, formData }: CourseChangeStateInput): Promise<void> {
      commit('setWaiting', true);

      const { href } = apiResolve({ params, name: 'course_change_state' });
      const [success, result, errors] = await apiRequestWithErrors(href, formData);

      commit('setErrors', errors);
      if (success) {
        commit('setCourseResponse', result?.course_edit);
      }
      commit('setWaiting', false);

      if (!success) {
        throw new Error('Course state change request not successful.');
      }
    },
    updateCourseContent({ commit }, updatedCourse: CourseEditInfo) {
      // Set these outside of the updater function so that the UI is updated instantly, both for
      // the something-is-happening indicator and for the course outline
      commit('setWaiting', true);
      // Update the value immediately
      commit('setValue', updatedCourse);
      // Do this so the request only happens once for multiple updates
      courseContentUpdater(async () => {
        const { href } = apiResolve({ name: 'course_update_content' });
        const [_, result, errors] = await apiRequestWithErrors(href, {
          course: courseUpdateContentInput(updatedCourse),
        });
        if (result?.course_edit) {
          // Update the value again. If we've created a new chapter/lesson, it'll have an id now
          commit('setValue', result?.course_edit);
        }
        commit('setErrors', errors);
        commit('setWaiting', false);
      });
    },

    addChapter({ dispatch, state }, { chapter }: CourseAddChapterInput) {
      if (!state.course) {
        return;
      }
      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      const newChapter = {
        ...chapter,
        slug: uniqueString(
          chapter.slug,
          state.course.chapters.map(c => c.slug),
        ),
      };
      updatedCourse.chapters = [...updatedCourse.chapters, newChapter];
      dispatch('updateCourseContent', updatedCourse);
    },

    updateChapter({ dispatch, state }, { chapterSlug, updatedChapter }: CourseUpdateChapterInput) {
      if (!state.course) {
        return;
      }
      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      updatedCourse.chapters = updatedCourse.chapters.map(chapter => {
        if (chapter.slug === chapterSlug) {
          return updatedChapter;
        }
        return chapter;
      });
      dispatch('updateCourseContent', updatedCourse);
    },

    updateContentOrder({ dispatch }, { content, chapter, course }: CourseUpdateContentOrderInput) {
      const updatedCourse: CourseEditInfo = deepCopyObj(course);

      // Overwrite lessons for the appropriate chapter
      updatedCourse.chapters = updatedCourse.chapters.map(courseChapter => {
        if (courseChapter.slug === chapter.slug) {
          return { ...courseChapter, content };
        }
        return courseChapter;
      });

      dispatch('updateCourseContent', updatedCourse);
    },

    toggleLessonPractice({ dispatch, state }, { lessonId }) {
      if (!state.course) {
        return;
      }

      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      updatedCourse.chapters = updatedCourse.chapters.map(courseChapter => {
        return {
          ...courseChapter,
          content: courseChapter.content.map((content: CourseEditContent) => {
            switch (content.kind) {
              case LESSON_KIND:
                if (content.lesson_id === lessonId) {
                  return { ...content, is_practice: !content.is_practice };
                } else return content;
              case PRACTICE_KIND:
                return content;
              default:
                unreachableCase(content);
            }
          }),
        };
      });

      dispatch('updateCourseContent', updatedCourse);
    },

    removeContent(
      { dispatch, state },
      { chapterSlug, removedContentSlug, removedContentKind }: CourseRemoveContentInput,
    ) {
      if (!state.course) {
        return;
      }
      // Filter out the removed lesson for the appropriate chapter
      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      updatedCourse.chapters = updatedCourse.chapters.map(courseChapter => {
        if (courseChapter.slug === chapterSlug) {
          return {
            ...courseChapter,
            content: courseChapter.content.filter(
              c => !(c.kind === removedContentKind && c.slug === removedContentSlug),
            ),
          };
        }
        return courseChapter;
      });

      dispatch('updateCourseContent', updatedCourse);
    },

    addContent({ dispatch, state }, { chapterSlug, newContent }: CourseAddContentInput) {
      if (!state.course) {
        return;
      }
      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      updatedCourse.chapters = updatedCourse.chapters.map(chapter => {
        if (chapter.slug === chapterSlug) {
          return { ...chapter, content: [...chapter.content, newContent] };
        }
        return chapter;
      });

      dispatch('updateCourseContent', updatedCourse);
    },

    async createLesson(
      { commit, dispatch, state, rootState },
      { chapterSlug, lessonTitle, addExampleContent }: CourseCreateLessonInput,
    ): Promise<boolean> {
      if (!state.course || !rootState.globals.user) {
        return false;
      }
      commit('setWaiting', true);
      const lessonSlug = slugify(lessonTitle);
      const inputs: LessonCreateApiInputs = {
        params: {
          chapterSlug,
          lessonSlug,
          courseSlug: state.course.slug,
        },
        formData: {
          title: lessonTitle,
          description: '',
          version_name: 'V0',
          lesson_slug: lessonSlug,
          author_id: rootState.globals.user.user_id.toString(),
          add_example_content: addExampleContent,
          for_v3_course: true,
        },
      };
      const { data, errors }: LessonCreateApiResult = await dispatch(
        'lessonEditor/createLesson',
        inputs,
        { root: true },
      );

      if (data?.lesson) {
        const addContentInput: CourseAddContentInput = {
          chapterSlug,
          newContent: {
            kind: 'lesson',
            lesson_id: data.lesson.lesson_id,
            slug: data.lesson_slug,
            lesson_version_id: data.lesson.version_id,
            version_name: data.lesson.version_name,
            state: data.lesson.state,
            title: data.lesson.title,
            description: data.lesson.description,
            image_url:
              data.lesson.image_url && data.lesson.image_url.length > 0
                ? data.lesson.image_url
                : undefined,
            is_practice: false,
          },
        };
        dispatch('addContent', addContentInput);
        return true;
      }

      commit('setErrors', errors);
      commit('setWaiting', false);
      return false;
    },

    reorderChapters({ dispatch, state }, { chapters }: CourseUpdateChapterOrderInput) {
      if (!state.course) {
        return;
      }
      const updatedCourse: CourseEditInfo = { ...deepCopyObj(state.course), chapters };
      dispatch('updateCourseContent', updatedCourse);
    },

    removeChapter({ dispatch, state }, { chapterSlug }: CourseRemoveChapterInput) {
      if (!state.course) {
        return;
      }
      const updatedCourse: CourseEditInfo = deepCopyObj(state.course);
      updatedCourse.chapters = updatedCourse.chapters.filter(
        chapter => chapter.slug !== chapterSlug,
      );
      dispatch('updateCourseContent', updatedCourse);
    },

    async pinUserToVersion(
      { commit },
      { params, formData }: CoursePinUserToVersionAPIInput,
    ): Promise<void> {
      commit('setWaiting', true);

      const { href } = apiResolve({ params, name: 'course_pin_user_to_version' });
      const [success, result, errors] = await apiRequestWithErrors(href, formData);
      if (success) {
        commit('setCourseResponse', result?.course_edit);
      } else {
        commit('setErrors', errors);
      }
      commit('setWaiting', false);
    },
  },

  getters: {
    courseEditRequestErrors(state: CourseEditState): string[] {
      return state.requestErrors;
    },
    courseEditWaitingOnRequest(state: CourseEditState): boolean {
      return state.waitingOnRequest;
    },
  },
};

export default courseEdit;
